import { useCallback, useEffect, useRef, useState } from "react";
import { packVisibleItems, unpackVisibleItems } from "@services/api";
import { USE_FIXTURES } from "../constants";
import {
  packVisibleItemsFixture,
  unpackVisibleItemsFixture,
} from "../__fixtures__/fixtureStore";
import { notifySuccess, notifyError } from "@components/ds/notify";

// Pack-visible / unpack-visible round-trips. Mirrors the web's
// packVisibleCrate() / confirmUnpack() exactly:
//
// - type derived from the crate (Shipment / Top SKU / Inventory titles → SHIPMENTS;
//   otherwise CRATE)
// - is_rescheduled + reschedule_crate_packing_id passed straight through
// - On 429 the server's error body usually carries a friendly message; we
//   surface it via notifyError verbatim (web does the same).
//
// Caller passes `reload` (from useCrateData) to refresh after success.

const SHIPMENT_TITLES = new Set([
  "Shipment Items",
  "Top SKU Items",
  "Inventory Items",
]);

const isShipmentCrate = (crate) =>
  SHIPMENT_TITLES.has((crate?.title || "").trim());

const collectUnpackedIds = (crate) =>
  (crate?.items || [])
    .filter((it) => !it?.packed_at && it?.order_item_id)
    .map((it) => it.order_item_id);

const collectPackedIds = (crate) =>
  (crate?.items || [])
    .filter((it) => Boolean(it?.packed_at) && it?.order_item_id)
    .map((it) => it.order_item_id);

const usePackUnpack = ({ reload } = {}) => {
  const [packing, setPacking] = useState(false);
  const [unpacking, setUnpacking] = useState(false);
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const pack = useCallback(
    async (crate) => {
      if (!crate) return false;
      const order_item_ids = collectUnpackedIds(crate);
      if (order_item_ids.length === 0) {
        notifyError("No visible items to pack");
        return false;
      }
      const isShipment = isShipmentCrate(crate);
      setPacking(true);
      try {
        if (USE_FIXTURES) {
          packVisibleItemsFixture({
            crate_id: crate?.crate_id || null,
            order_item_ids,
          });
        } else {
          await packVisibleItems({
            type: isShipment ? "SHIPMENTS" : "CRATE",
            crate_id: isShipment ? null : crate?.crate_id || null,
            order_item_ids,
            is_rescheduled: Boolean(crate?.is_rescheduled),
            reschedule_crate_packing_id:
              crate?.reschedule_crate_packing_id || null,
          });
        }
        notifySuccess("Packing requested");
        if (reload) await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to pack";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setPacking(false);
      }
    },
    [reload],
  );

  const unpack = useCallback(
    async (crate) => {
      if (!crate) return false;
      const order_item_ids = collectPackedIds(crate);
      if (order_item_ids.length === 0) {
        notifyError("No packed items to unpack");
        return false;
      }
      const isShipment = isShipmentCrate(crate);
      setUnpacking(true);
      try {
        if (USE_FIXTURES) {
          unpackVisibleItemsFixture({
            crate_id: crate?.crate_id || null,
            order_item_ids,
          });
        } else {
          await unpackVisibleItems({
            type: isShipment ? "SHIPMENTS" : "CRATE",
            crate_id: isShipment ? null : crate?.crate_id || null,
            order_item_ids,
            is_rescheduled: Boolean(crate?.is_rescheduled),
            reschedule_crate_packing_id:
              crate?.reschedule_crate_packing_id || null,
          });
        }
        notifySuccess("Unpacking completed");
        if (reload) await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to unpack";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setUnpacking(false);
      }
    },
    [reload],
  );

  return { pack, unpack, packing, unpacking };
};

export default usePackUnpack;
